import React from 'react';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookHeading from '../../../components/playbooks/PlaybookHeading';

const ManagerCommunityStage3 = () => {
  return (
    <div className="max-w-none text-gray-100">
      <PlaybookHeading
        title="Stage 3: Establishment"
        subtitle="Facilitate the kick-off and establish the community of practice."
      />
      
      <ExpandableCallout
        type="info"
        title="What do I need to DO in this stage?"
        defaultOpen={true}
      >
        <p className="text-base text-gray-300 leading-relaxed">
          You need to do 2 things:
        </p>
        <ol className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">1.</span>
            <span>Facilitate a kick off and agree: ways of working + the next best action.</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">2.</span>
            <span>Agree what the CoP should action first.</span>
          </li>
        </ol>
        <p className="mt-4 text-base text-gray-300 leading-relaxed">Here's what we mean…</p>
      </ExpandableCallout>

      <div className="mt-8 space-y-6">
        <h2 className="text-xl font-semibold text-white mb-4">Kick Off Call And The Next Best Action</h2>
        <p className="text-base text-gray-300 leading-relaxed">
          So you've got folks excited and interested about the prospect of belonging to a people manager community of practice…now what?
        </p>
        <p className="text-base text-gray-300 leading-relaxed">
          Well, you can do one of two things, you can either:
        </p>
        <ol className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">1.</span>
            <span>Do a ways of working session and agree things like purpose, when and how you'll meet up etc…</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">2.</span>
            <span>Ideate and prioritise your first win together</span>
          </li>
        </ol>
        <p className="text-base text-gray-300 leading-relaxed">
          Whichever you choose, I'd recommend doing the other one next 🙂
        </p>

        <ExpandableCallout
          type="tip"
          title="Key Success Factor"
          defaultOpen={true}
        >
          <p className="text-base text-gray-300 leading-relaxed">
            It's a very good idea to co-facilitate this with your people manager angel. This isn't an HR thing. This is a people manager thing right.
          </p>
        </ExpandableCallout>
      </div>

      <div className="mt-8 space-y-6">
        <h2 className="text-xl font-semibold text-white mb-4">Community Charter</h2>
        <p className="text-base text-gray-300 leading-relaxed">
          If you choose this route first, then the focus of the kick-off is about aligning together on:
        </p>
        <ul className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Mission and purpose</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Who will facilitate the sessions (rotation or a specific person responsible)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Tools we'll use (where will we track projects, will we have a private slack channel etc)</span>
          </li>
        </ul>

        <p className="text-base text-gray-300 leading-relaxed mt-4">
          Adapting a team charter [miro template] is a good way to facilitate this:
        </p>
        <ol className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">1.</span>
            <span>Prepare a miro canvas or use a wall with stickies if you're in-office</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">2.</span>
            <span>Invite folks to add sticky notes to each area</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">3.</span>
            <span>Spend 5 mins on each area unpacking and consolidating ideas</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">4.</span>
            <span>Move on to next area</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">5.</span>
            <span>Repeat until complete</span>
          </li>
        </ol>

        <p className="text-base text-gray-300 leading-relaxed mt-4">
          Leaving this session you should have:
        </p>
        <ol className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">1.</span>
            <span>A completed community charter</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">2.</span>
            <span>Agreed next steps and owners</span>
          </li>
        </ol>
      </div>

      <div className="mt-8 space-y-6">
        <h2 className="text-xl font-semibold text-white mb-4">Our First Win</h2>
        <p className="text-base text-gray-300 leading-relaxed">
          If you choose this route first, then the focus is going to be more around using the data and insights from the memo to ideate on:
        </p>
        <p className="text-base text-gray-300 leading-relaxed">
          What are the most impactful initiatives we should run with
        </p>
        <p className="text-base text-gray-300 leading-relaxed">
          A simple impact/effort matrix [miro template] is a great way to get folks working together to prioritise these.
        </p>

        <p className="text-base text-gray-300 leading-relaxed mt-4">
          The outputs of this session start to form the backbone for a crowd-sourced agenda for future community of practice sessions (handy!) and might include things like:
        </p>
        <ul className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>manager role definition alignment</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>feedback dojo</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Building a better feedback loop between teams &lt;&gt; managers &lt;&gt; People team</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>managing performance expectations dojo</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>conflict management 101</span>
          </li>
        </ul>

        <p className="text-base text-gray-300 leading-relaxed mt-4">
          Leaving this session you should have:
        </p>
        <ol className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">1.</span>
            <span>A prioritised grid of initiatives and/or sessions for the community to go after</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">2.</span>
            <span>Clarity on next steps and ownership for the highest ranking item</span>
          </li>
        </ol>

        <ExpandableCallout
          type="tip"
          title="Key Success Factor"
          defaultOpen={true}
        >
          <p className="text-base text-gray-300 leading-relaxed">
            Regardless of which route you choose, the key here is to facilitate but not lead. This can never be "your thing". To self-sustain, a community of practice needs to run from within and that means people managers themselves stepping up to own it, which is why we approach it like a change initiative rather than a people project.
          </p>
        </ExpandableCallout>
      </div>

      <div className="mt-8 space-y-6">
        <h2 className="text-xl font-semibold text-white mb-4">Recap</h2>
        <p className="text-base text-gray-300 leading-relaxed">
          By the end of this stage you should have:
        </p>
        <ul className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Co-facilitated (with your people manager angel) a group call with the people manager community of practice where you chose 1 of the 2 starter templates (community charter or our first win)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Teed up the next meeting #momentum</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ManagerCommunityStage3; 